import { PlusOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Empty, Form, Image, message, Upload } from 'antd';
import {
  debounce,
  filter,
  includes,
  isArray,
  isNumber,
  map,
  take,
} from 'lodash';
import { evaluate } from 'mathjs';
import moment from 'moment';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../../AppContext';
import {
  DeleteButton,
  DragIcon,
  EditButton,
} from '../../../../../../assets/svg';
import {
  ALLOWED_ACTION_KEYS,
  ALLOWED_ACTION_TYPE,
  ALLOWED_IMAGE_TYPES,
  BREAKPOINTS,
  DATETIMEWITHDIVIDE,
  DEFAULT_PAGE_SIZE,
  DEFAULTDATEFORMAT,
  ELEMENT_IDS,
  FALLBACK_PDF_URL,
  FIELD_TYPES,
  FILE_SIZE_8MB,
  INVALID_FIELD_VALUES,
  PROJECT_ROLES,
  REGEX,
  ROUTES,
  SCROLL_PERCENT,
  TAB_KEYS,
  USER_ROLES,
} from '../../../../../../common/constants';
import useRouter from '../../../../../../common/useRouter';
import {
  addWatermark,
  compressImage,
  dataURLtoFile,
  fileUpload,
  formatNumericInput,
  toCamelCase,
} from '../../../../../../common/utils';
import CanPerform from '../../../../../../components/CanPerform';
import CommonCard from '../../../../../../components/CommonCard';
import CommonTable from '../../../../../../components/CommonTable';
import CommonTooltip from '../../../../../../components/CommonTooltip';
import { GET_URLS } from '../../../../../approver/graphql/Queries';
import {
  CREATE_REGISTER_TABLE_FIELDS,
  UPDATE_REGISTER_FORM_FIELD_SEQUENCES,
  UPDATE_REGISTER_LOG_DETAIL_ENTRY,
} from '../../../../graphql/Mutation';
import {
  GET_REGISTER,
  GET_REGISTER_LOG_FORM_FIELDS_ENTRY_LIST,
} from '../../../../graphql/Queries';
import AddEditTableFieldModal from './AddEditTableFieldModal';
import AddFieldModal from './AddFieldModal';
import DeleteFormFieldRecordModal from './DeleteFormFieldRecordModal';
import FieldIcon from './FieldIcon';
import DeleteTableFieldModal from './registerDetails/DeleteTableFieldModal';
import RenderFields from './RenderFields';

const ColumnContext = React.createContext({});

const EditableCell = ({
  record,
  dataIndex,
  title,
  fieldId,
  fieldType,
  editing,
  isRequired,
  options = [],
  index,
  fieldsData,
  onFieldClick,
  isFieldClicked,
  children,
  isMobileViewport,
  isAuditor,
  isUserAssignedToProject,
  isOptionsFocused,
  setIsOptionsFocused,
  isMultiSelectOptionsFocused,
  setIsMultiSelectOptionsFocused,
  isDateFocused,
  setIsDateFocused,
  onDateOpenChange,
  isDisabled,
  handleFieldChange,
  handleBlurSave,
  handleFileListRefUpdate,
  form,
  tableHeaders,
  fileListRef,
  deletingFile,
  setDeletingFile,
  uploadKey,
  handleOnChange,
  handlePreview,
  calculateFieldValue,
  handleUploadRemove,
  ...restProps
}) => {
  const fieldOptions = useMemo(
    () =>
      map(options, (option) => ({
        label: option,
        value: option,
      })),
    [options],
  );

  const fieldName = `${index}_${dataIndex}`;

  const handleTextChange = useCallback(
    (e) => {
      handleFieldChange({
        [`${index}_${dataIndex}`]: e?.target?.value,
      });
    },
    [handleFieldChange, index, dataIndex],
  );

  const handleTextBlur = useCallback(() => {
    if (isMobileViewport) return;
    handleBlurSave(index, record);
  }, [isMobileViewport, handleBlurSave, index, record]);

  const handleNumericChange = useCallback(
    (e) => {
      const value = e?.target?.value;
      const formattedValue = formatNumericInput(value);
      handleFieldChange({
        [`${index}_${dataIndex}`]: formattedValue,
      });
    },
    [handleFieldChange, index, dataIndex],
  );

  const handleUploadChange = (info) => {
    const isRemoveOperation =
      info?.fileList?.length < fileListRef?.current[fieldName]?.length;

    if (isRemoveOperation) {
      const newFileList = info?.fileList?.map((file) => ({
        ...file,
        status: 'done',
      }));
      handleFileListRefUpdate(newFileList, fieldName);

      if (fieldsData?.current?.[index]) {
        // eslint-disable-next-line no-param-reassign
        fieldsData.current[index] = {
          ...fieldsData?.current[index],
          [index]: newFileList?.map((file) => ({
            uid: file?.uid,
            status: file?.status,
            url: file?.url,
            value: file?.url,
            key: file?.key,
            name: file?.name,
            thumbUrl: file?.thumbUrl,
          })),
        };
      }
      form?.setFieldsValue({
        [fieldName]: newFileList,
      });
      return;
    }
    const newFileList = info?.fileList?.slice(-3)?.map((file) => ({
      ...file,
      status: 'done',
    }));
    if (newFileList?.length > 3) {
      message.warning('Maximum 3 files are allowed');
      return;
    }
    handleFileListRefUpdate(newFileList, fieldName);

    if (fieldsData?.current?.[index]) {
      // eslint-disable-next-line no-param-reassign
      fieldsData.current[index] = {
        ...fieldsData?.current[index],
        [index]: newFileList?.map((file) => ({
          uid: file?.uid,
          status: file?.status,
          url: file?.url,
          value: file?.url,
          key:
            file?.key ||
            (file?.name ? `register/attachments/${file?.name}` : undefined),
          name: file?.name,
          thumbUrl: file?.thumbUrl,
        })),
      };
    }

    const hasNewFiles = newFileList?.some((file) => !file?.url && !file?.key);
    if (hasNewFiles) {
      handleOnChange(info, fieldName, record, dataIndex, index);
    }
  };

  return (
    <td {...restProps}>
      {editing ? (
        <RenderFields
          fieldType={fieldType}
          fieldIndex={`${index}_${dataIndex}`}
          fieldIsRequired={isRequired && !record?.isEmptyRow}
          className="m-0"
          initialFieldValue={record[dataIndex]}
          fieldDisabled={isAuditor || isDisabled || !isUserAssignedToProject}
          divClassName="width-200"
          onTextChange={handleTextChange}
          onTextBlur={handleTextBlur}
          onFieldClick={onFieldClick}
          isFieldClicked={isFieldClicked}
          options={fieldOptions}
          onNumericChange={handleNumericChange}
          onNumericBlur={() => {
            if (isMobileViewport) return;
            handleBlurSave(index, record);
          }}
          dateOpen={isDateFocused === `${index}_${dataIndex}`}
          onDateFocused={() => {
            if (!isMobileViewport) {
              setIsDateFocused(`${index}_${dataIndex}`);
            }
          }}
          onDateOpenChange={onDateOpenChange}
          onDateChange={(date) => {
            handleFieldChange({
              [`${index}_${dataIndex}`]: date,
            });
          }}
          optionsOpen={isOptionsFocused === `${index}_${dataIndex}`}
          onOptionsFocus={() => {
            if (!isMobileViewport) {
              setIsOptionsFocused(`${index}_${dataIndex}`);
            }
          }}
          onOptionBlur={() => {
            if (!isMobileViewport) {
              setIsOptionsFocused(null);
            }
          }}
          popupClassName={isMobileViewport && 'd-none'}
          onOptionChange={(value) => {
            setIsOptionsFocused(null);
            handleFieldChange({
              [`${index}_${dataIndex}`]: value || undefined,
            });
            handleBlurSave(index, record);
          }}
          onOptionDropdownVisibilityChange={(e) => {
            if (!e) {
              setIsOptionsFocused(null);
            } else {
              setIsOptionsFocused(`${index}_${dataIndex}`);
            }
          }}
          optionClassName="form-field-select"
          multiSelectOpen={
            isMultiSelectOptionsFocused === `${index}_${dataIndex}`
          }
          onMultiSelectFocus={() => {
            if (!isMobileViewport) {
              setIsMultiSelectOptionsFocused(`${index}_${dataIndex}`);
            }
          }}
          onMultiSelectBlur={() => {
            if (!isMobileViewport) {
              setIsMultiSelectOptionsFocused(null);
              handleBlurSave(index, record);
            }
          }}
          onMultiSelectDropdownVisibilityChange={(e) => {
            if (!e) {
              setIsMultiSelectOptionsFocused(null);
            } else {
              setIsMultiSelectOptionsFocused(`${index}_${dataIndex}`);
            }
          }}
          onMultiSelectChange={(value) => {
            const newValue = value?.length > 0 ? value : undefined;
            handleFieldChange({ [`${index}_${dataIndex}`]: newValue });
          }}
          getPopupContainer={() =>
            // eslint-disable-next-line no-undef
            document.querySelector('.ant-table-content')
          }
          formClassName="m-0"
          calculateFieldValue={() =>
            calculateFieldValue(`${index}_${dataIndex}`)
          }
          onCalculationChange={(e) =>
            handleFieldChange({
              [`${index}_${dataIndex}`]: e?.target?.value,
            })
          }
          onCalculationBlur={() => {
            if (isMobileViewport) return;
            handleBlurSave(index, record);
          }}
          fileListRef={fileListRef}
          openFileDialogOnClick={!isMobileViewport}
          records={record}
          uploadDataIndex={dataIndex}
          uploadIndex={index}
          uploadFieldName={`${index}_${dataIndex}`}
          handleUploadChange={handleUploadChange}
          uploadKey={`${uploadKey}-${fieldName}`}
          beforeUpload={(file) => {
            const currentFiles = fileListRef?.current[fieldName] || [];
            if (currentFiles?.length >= 3) {
              message.warning('Maximum 3 files are allowed');
              return Upload.LIST_IGNORE;
            }
            if (file?.size > FILE_SIZE_8MB) {
              message.destroy();
              message.error('Maximum file size is 8MB');
              return Upload.LIST_IGNORE;
            }
            const newFile = file;
            if (file?.type?.startsWith('image/')) {
              newFile.thumbUrl = URL?.createObjectURL(file);
            } else if (file?.type === 'application/pdf') {
              newFile.thumbUrl = FALLBACK_PDF_URL;
            }
            return false;
          }}
          onUploadRemove={handleUploadRemove}
        />
      ) : (
        <div>{children}</div>
      )}
    </td>
  );
};

const Columns = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props?.column?.id || props?.dataIndex,
  });

  const style = {
    ...props?.style,
    transform: CSS.Translate?.toString(transform),
    transition,
    ...(isDragging
      ? {
          position: 'relative',
          zIndex: 9999,
        }
      : {}),
  };

  const contextValue = useMemo(
    () => ({
      setActivatorNodeRef,
      listeners,
    }),
    [setActivatorNodeRef, listeners],
  );

  return (
    <ColumnContext.Provider value={contextValue}>
      <th {...props} ref={setNodeRef} style={style} {...attributes} />
    </ColumnContext.Provider>
  );
};

const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(ColumnContext);
  return (
    <CanPerform
      action={ALLOWED_ACTION_KEYS.ADD_PROJECT_REGISTER}
      type={ALLOWED_ACTION_TYPE.BOTH}
    >
      <CommonTooltip title="Change order">
        <DragIcon
          className="cursor-move"
          ref={setActivatorNodeRef}
          {...listeners}
        />
      </CommonTooltip>
    </CanPerform>
  );
};

const AddEditRegisterFieldsTable = React.memo(
  ({
    tableHeaders,
    setTableHeaders,
    tableColumns,
    setTableColumns,
    isEditControl,
    isEditable,
    isDisabled,
    isUserAssignedToProject,
    showDetailPrompt,
    showTableFieldsPrompt,
    handleTableRouterPromptUpdate,
  }) => {
    const {
      location,
      params: { projectId, registerId, registerLogId },
    } = useRouter();
    const { dispatch, getCurrentUserRole, getCurrentProjectDetail, state } =
      useContext(AppContext);
    const currentProjectDetails = getCurrentProjectDetail();
    const initialPagination = {
      skip: 0,
      limit: DEFAULT_PAGE_SIZE,
    };
    const [form] = Form.useForm();
    const [numericFields, setNumericFields] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [totalTableDataCount, setTotalTableDataCount] = useState(0);
    const fieldsData = useRef([]);
    const [isLoading, setIsLoading] = useState(false);
    const [scrollFlag, setScrollFlag] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [selectedField, setSelectedField] = useState(false);
    const [showDeleteRecordModal, setShowDeleteRecordModal] = useState(false);
    const [selectedRecordToDelete, setSelectedRecordToDelete] = useState();
    const [isOptionsFocused, setIsOptionsFocused] = useState(null);
    const [isMultiSelectOptionsFocused, setIsMultiSelectOptionsFocused] =
      useState(null);
    const [isDateFocused, setIsDateFocused] = useState(null);
    const [isFieldClicked, setIsFieldClicked] = useState(null);
    const [editingKeys, setEditingKeys] = useState(new Set());
    const imageKeys = useRef([]);
    const tempUploadDataRef = useRef();
    const [deletingFile, setDeletingFile] = useState(false);
    const fileListRef = useRef({});
    const [uploadKey, setUploadKey] = useState(0);
    const [isCreatingEntry, setIsCreatingEntry] = useState(false);
    const [rowRecord, setRowRecord] = useState();
    const [fieldIndex, setFieldIndex] = useState();
    const [showFillModal, setShowFillModal] = useState(false);
    const [isFormChanged, setIsFormChanged] = useState(false);
    const createEntryTimeoutRef = useRef(null);
    const tableRef = useRef();
    const focusedFieldKeyRef = useRef(null);
    const isMobileViewport = useMedia(`(max-width: ${BREAKPOINTS.mobile}px)`);
    const isAuditor =
      getCurrentUserRole() === USER_ROLES.EXTERNAL_AUDITOR ||
      state?.projectRole === PROJECT_ROLES.AUDITOR;

    const showRouterPrompt = (value) => {
      dispatch({
        type: 'SET_SHOW_PROMPT',
        data: value,
      });
    };

    const calculateFieldValue = (fieldName) => {
      const [rowIndex, name] = fieldName?.split('_');
      const values = form?.getFieldsValue();
      const formulas = tableHeaders
        ?.filter((item) => item?.index === name)
        ?.flatMap((item) => item?.options || [])
        ?.map((str) => str?.replace(/"/g, ''))?.[0];
      if (!formulas) return '';
      try {
        const modifiedExpValue = formulas?.replace(REGEX.UUID, (match) => {
          return values?.[
            `${rowIndex}_${
              filter(tableHeaders, (record) => match === record?.id)?.[0]?.index
            }`
          ];
        });
        const result = evaluate(modifiedExpValue);
        return isNumber(result) ? Number(result)?.toFixed(2) : '';
      } catch (error) {
        return '';
      }
    };

    const [getRegisterLogFormFieldsEntryList, { loading }] = useLazyQuery(
      GET_REGISTER_LOG_FORM_FIELDS_ENTRY_LIST,
      {
        fetchPolicy: 'network-only',
        onError() {},
        onCompleted(response) {
          const total = response?.registerLogFormFieldEntryList?.total;
          setTotalTableDataCount(total);
          const data = response?.registerLogFormFieldEntryList?.data;
          fileListRef.current = {};
          setUploadKey((prev) => prev + 1);
          const transformedData =
            data?.map((item) => {
              const transformedItem = {
                key: item?.id?.toString(),
                id: item?.id || '',
                ...Object.entries(item?.responses || {})?.reduce(
                  (acc, [key, value]) => {
                    const field = tableColumns?.find(
                      (col) => col?.dataIndex === toCamelCase(key),
                    );
                    if (field?.type === FIELD_TYPES.DATE) {
                      if (value) {
                        acc[toCamelCase(key)] = moment(
                          value,
                          DEFAULTDATEFORMAT,
                        )?.isValid()
                          ? moment(value, DEFAULTDATEFORMAT)
                          : null;
                      } else {
                        acc[toCamelCase(key)] = null;
                      }
                    } else if (field?.type === FIELD_TYPES.CHECKBOXES) {
                      const values = value
                        ? value?.split(',')?.filter(Boolean)
                        : [];
                      acc[toCamelCase(key)] =
                        values?.length > 0 ? values : undefined;
                    } else if (field?.type === FIELD_TYPES.OPTIONS) {
                      acc[toCamelCase(key)] = value || undefined;
                    } else {
                      acc[toCamelCase(key)] = value;
                    }
                    return acc;
                  },
                  {},
                ),
              };
              return transformedItem;
            }) || [];

          const newRow = tableColumns?.reduce(
            (acc, field) => {
              acc[field?.dataIndex] = undefined;
              return acc;
            },
            { key: `new-${Date.now()}`, isEmptyRow: true },
          );

          const reversedData = [...transformedData]?.reverse();
          if (
            !isDisabled &&
            !isAuditor &&
            [...tableData, ...reversedData]?.length === total
          ) {
            reversedData?.push(newRow);
          }
          fieldsData.current = [...tableData, ...reversedData];
          setEditingKeys((prev) => new Set([...prev, newRow.key]));
          if (scrollFlag) {
            const dataList = [...tableData, ...reversedData];
            setTableData(dataList);
            setScrollFlag(false);
          } else {
            const dataList = [...reversedData];
            setTableData(dataList);
          }
          setHasMore(!!data?.length);

          const columns =
            response?.registerLogFormFieldEntryList?.registerFormFields || [];
          const filteredColumns = map(columns, (column) => {
            return {
              ...column,
              name: column?.name,
              dataIndex: toCamelCase(column?.name),
              index: toCamelCase(column?.name),
            };
          });
          setTableHeaders(filteredColumns);
          setIsLoading(false);
        },
      },
    );

    const [getRegister] = useLazyQuery(GET_REGISTER, {
      fetchPolicy: 'network-only',
      onError() {},
      onCompleted(response) {
        const data = response?.getRegister?.registerFormFields;
        const filteredColumns = map(data, (column) => ({
          ...column,
          dataIndex: toCamelCase(column?.name),
          index: toCamelCase(column?.name),
        }));
        setTableHeaders(filteredColumns);
      },
    });

    const handleRegisterRefetch = () => {
      getRegister({ variables: { getRegisterId: registerId } });
    };

    const [createRegisterLogFormFieldEntry] = useMutation(
      CREATE_REGISTER_TABLE_FIELDS,
      {
        fetchPolicy: 'network-only',
        onError() {},
        onCompleted() {
          handleTableRouterPromptUpdate(false);
          showRouterPrompt(
            showDetailPrompt?.current || showTableFieldsPrompt?.current,
          );
        },
      },
    );

    const [updateRegisterFormFieldSequences] = useMutation(
      UPDATE_REGISTER_FORM_FIELD_SEQUENCES,
      {
        onError() {},
        onCompleted: () => {
          if (
            location?.pathname !==
            `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/edit-control`
          ) {
            getRegisterLogFormFieldsEntryList({
              variables: { registerLogId, filter: initialPagination },
            });
          }
          handleRegisterRefetch();
        },
      },
    );

    const [updateRegisterLogFormFieldEntry] = useMutation(
      UPDATE_REGISTER_LOG_DETAIL_ENTRY,
      {
        fetchPolicy: 'network-only',
        onError() {},
        onCompleted() {
          handleTableRouterPromptUpdate(false);
          showRouterPrompt(
            showDetailPrompt?.current || showTableFieldsPrompt?.current,
          );
        },
      },
    );

    const [fetchSignedUrl] = useLazyQuery(GET_URLS, {
      fetchPolicy: 'network-only',
      onCompleted: async (response) => {
        const promises = map(
          response?.getSignedPutUrls?.signedUrls,
          async (imageUrl, index) => {
            const objectFiles = tempUploadDataRef?.current?.filter(
              (item) => item?.originFileObj,
            );
            const callBack = async (image) => {
              const watermarkedImageUrl = await addWatermark(image);
              const file = dataURLtoFile(
                watermarkedImageUrl,
                objectFiles?.[index]?.originFileObj?.name,
              );
              await fileUpload(imageUrl, file)?.catch(() => {
                return message.error('Image upload failed!');
              });
            };
            if (
              includes(
                [...ALLOWED_IMAGE_TYPES],
                objectFiles?.[index]?.originFileObj?.type,
              )
            ) {
              await compressImage({
                file: objectFiles?.[index]?.originFileObj,
                fileType: objectFiles?.[index]?.type?.split('/')?.[1],
                callBack,
              });
            } else {
              await fileUpload(
                imageUrl,
                objectFiles[index]?.originFileObj,
              )?.catch(() => {
                return message.error('File upload failed!');
              });
            }
          },
        );
        await Promise.all(promises);
      },
      onError() {
        message.error('Error getting upload URL');
      },
    });

    const mouseSensor = useSensor(MouseSensor);
    const touchSensor = useSensor(TouchSensor);
    const keyboardSensor = useSensor(KeyboardSensor);
    const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

    useEffect(() => {
      return () => {
        form.resetFields();
      };
    }, []);

    useEffect(() => {
      if (
        location?.pathname !==
        `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/edit-control`
      ) {
        setIsLoading(true);
        getRegisterLogFormFieldsEntryList({
          variables: { registerLogId, filter: initialPagination },
        });
      }
    }, [registerLogId]);

    useEffect(() => {
      if (
        tableColumns?.length > 0 &&
        location?.pathname ===
          `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/edit-control`
      ) {
        const filteredColumns = map(tableColumns, (column) => ({
          ...column,
          dataIndex: toCamelCase(column?.name),
          index: toCamelCase(column?.name),
        }));
        setTableHeaders(filteredColumns);
      }
    }, [tableColumns]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        // eslint-disable-next-line no-undef
        const dropdowns = document.querySelectorAll(
          '.ant-select-dropdown, .ant-picker-dropdown, .ant-dropdown',
        );

        const isInsideDropdown = Array?.from(dropdowns)?.some((dropdown) =>
          dropdown?.contains(event?.target),
        );

        const isFieldControl = event?.target?.closest(
          '.ant-select-clear, .ant-picker-clear, .ant-select-selection-item-remove, .anticon',
        );

        if (isInsideDropdown || isFieldControl) return;

        if (tableRef?.current && !tableRef?.current?.contains(event?.target)) {
          setEditingKeys((prevKeys) => {
            const newKeys = new Set(
              [...prevKeys]?.filter((key) =>
                fieldsData?.current?.some(
                  (row) => row?.key === key && row?.isEmptyRow,
                ),
              ),
            );
            return newKeys;
          });
        }
      };

      // eslint-disable-next-line no-undef
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        // eslint-disable-next-line no-undef
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    const handleFieldChange = useCallback(
      (changedFields) => {
        const [fieldName, value] = Object?.entries(changedFields)?.[0];
        const [rowIndex, fieldType] = fieldName?.split('_');
        focusedFieldKeyRef.current = fieldName;
        const changedField = tableHeaders?.find(
          (field) => field?.dataIndex === fieldType,
        );
        let formattedValue = value;
        if (changedField?.type === FIELD_TYPES.NUMERIC) {
          formattedValue = formatNumericInput(value);
        }
        form?.setFieldValue(fieldName, formattedValue);
        if (changedField?.type === FIELD_TYPES.NUMERIC) {
          const calculationFields = tableHeaders
            ?.filter((field) => field?.type === FIELD_TYPES.CALCULATION)
            ?.map((field) => field?.dataIndex);

          if (calculationFields?.length) {
            calculationFields?.forEach((calcField) => {
              const calcFieldName = `${rowIndex}_${calcField}`;
              const calculatedValue = calculateFieldValue(calcFieldName);
              form?.setFieldValue(calcFieldName, calculatedValue);
              const isRequiredCalcField = tableHeaders.find(
                (f) => f?.dataIndex === calcField && f?.isRequired,
              );
              if (
                isRequiredCalcField &&
                (calculatedValue === '' || calculatedValue === undefined)
              ) {
                form?.setFields([
                  {
                    name: calcFieldName,
                    errors: ['This field is required'],
                  },
                ]);
              } else {
                form?.setFields([
                  {
                    name: calcFieldName,
                    errors: [],
                  },
                ]);
              }
              if (fieldsData?.current?.[parseInt(rowIndex, 10)]) {
                fieldsData.current[parseInt(rowIndex, 10)][calcField] =
                  calculatedValue;
              }
            });
          }
        }

        const updatedData = [...fieldsData?.current];
        const rowIndexNum = parseInt(rowIndex, 10);
        if (updatedData?.[rowIndexNum]) {
          updatedData[rowIndexNum] = {
            ...updatedData[rowIndexNum],
            [fieldType]: formattedValue,
          };

          const isLastRow = rowIndexNum === updatedData?.length - 1;
          const lastRow = updatedData[updatedData?.length - 1];
          if (
            isLastRow &&
            lastRow?.isEmptyRow &&
            formattedValue !== null &&
            formattedValue !== ''
          ) {
            const newRow = tableHeaders?.reduce(
              (acc, field) => {
                acc[field?.dataIndex] = null;
                return acc;
              },
              { key: `new-${Date.now()}`, isEmptyRow: true },
            );
            updatedData?.push(newRow);
            setTableData((prev) => [...prev, newRow]);
            setEditingKeys((prev) => new Set([...prev, newRow.key]));
          }
          fieldsData.current = updatedData;
        }
      },
      [tableHeaders, form],
    );

    const getDateData = (column, value, fieldName) => {
      let fieldValue = value;

      if (fieldValue) {
        if (moment(fieldValue)?.isValid()) {
          fieldValue = moment(fieldValue)?.format(DEFAULTDATEFORMAT);
        } else {
          form.setFields([
            {
              name: fieldName,
              errors: ['Please enter a valid date'],
            },
          ]);
          return 'Error';
        }
      } else if (column?.isRequired) {
        form.setFields([
          {
            name: fieldName,
            errors: ['This field is required'],
          },
        ]);
        return 'Error';
      }
      return fieldValue;
    };

    const getCheckBoxesData = (column, value, fieldName) => {
      let fieldValue = value;
      if (column?.type === FIELD_TYPES.CHECKBOXES) {
        if (
          !fieldValue ||
          (Array.isArray(fieldValue) && fieldValue?.length === 0)
        ) {
          return '';
        }
        if (fieldValue) {
          fieldValue = isArray(fieldValue)
            ? fieldValue?.filter(Boolean)?.join(',')
            : fieldValue;
        }
      } else if (column?.type === FIELD_TYPES.OPTIONS) {
        if (!fieldValue) {
          return '';
        }
      }

      if (column?.type === FIELD_TYPES.DATE) {
        const data = getDateData(column, fieldValue, fieldName);
        if (data === 'Error') {
          return 'Error';
        }
        fieldValue = data;
      }

      if (column?.type === FIELD_TYPES.ATTACHMENT) {
        const [rowIndex, name] = fieldName?.split('_');
        const currentData = fieldsData?.current?.[rowIndex]?.[name];
        let updatedKeys = [];
        if (Array.isArray(currentData)) {
          updatedKeys =
            currentData
              ?.filter((item) => item?.key)
              ?.map((item) => item?.key) || [];
        } else if (typeof currentData === 'string') {
          updatedKeys = currentData?.split(',')?.filter(Boolean);
        }

        fieldValue = [...updatedKeys];
        fieldValue = take(fieldValue, 3)?.join(',');
      }
      return fieldValue;
    };

    const createResponsesForEntry = (index, allValues, record) => {
      const responses = [];
      tableHeaders?.forEach((column) => {
        const entryFieldName = `${index}_${column?.dataIndex}`;
        let fieldValue = allValues[entryFieldName];

        if (column?.type === FIELD_TYPES.ATTACHMENT && record?.id) {
          const attachmentData = record[column?.dataIndex];
          if (Array.isArray(attachmentData)) {
            const currentFileList = fileListRef?.current[entryFieldName] || [];
            const fileKeys = currentFileList
              ?.filter((item) => item?.key || item?.name || item?.url)
              ?.map((item) => {
                if (item?.key) return item?.key;
                if (item?.name) return `register/attachments/${item?.name}`;
                return item?.url;
              })
              ?.filter(Boolean);

            fieldValue = take([...fileKeys], 3)?.join(',');
            responses?.push({
              registerFormFieldId: column?.id,
              value: fieldValue || '',
            });
            return;
          }
        }

        const data = getCheckBoxesData(column, fieldValue, entryFieldName);
        if (data === 'Error') {
          return 'Error';
        }
        fieldValue = data;

        if (column?.type === FIELD_TYPES.ATTACHMENT) {
          imageKeys.current[entryFieldName] = [];
        }

        if (record?.id) {
          responses?.push({
            registerFormFieldId: column?.id,
            value:
              fieldValue === null || fieldValue === undefined
                ? ''
                : String(fieldValue),
          });
        } else if (
          !column?.isRequired ||
          (fieldValue !== undefined && fieldValue !== null && fieldValue !== '')
        ) {
          responses?.push({
            registerFormFieldId: column?.id,
            value: Array.isArray(fieldValue)
              ? fieldValue
              : String(fieldValue || ''),
          });
        }
      });
      return responses;
    };

    const handleBlurSave = useCallback(
      async (rowIndex, record) => {
        const allValues = form?.getFieldsValue();
        const rowFieldKeys = Object.keys(allValues)?.filter((key) =>
          key?.startsWith(`${rowIndex}`),
        );

        const isRowEmpty = rowFieldKeys?.every((key) => {
          const val = allValues[key];
          return (
            includes([...INVALID_FIELD_VALUES, '-'], val) ||
            (Array.isArray(val) && val?.length === 0)
          );
        });

        if (isRowEmpty) {
          const clearedFields = rowFieldKeys?.map((key) => ({
            name: key,
            errors: [],
          }));
          form?.setFields(clearedFields);
          handleTableRouterPromptUpdate(false);
          showRouterPrompt(false);
          return;
        }

        try {
          await form?.validateFields(rowFieldKeys, { validateOnly: true });

          if (createEntryTimeoutRef?.current) {
            clearTimeout(createEntryTimeoutRef?.current);
          }
          createEntryTimeoutRef.current = setTimeout(async () => {
            const requiredFields = tableHeaders?.filter(
              (field) => field?.isRequired,
            );
            const invalidFields = [];
            requiredFields?.forEach((field) => {
              const fieldName = `${rowIndex}_${field?.dataIndex}`;
              const fieldValue = allValues[fieldName];

              const isEmpty =
                !fieldValue ||
                fieldValue === '' ||
                fieldValue === '-' ||
                (Array.isArray(fieldValue) && fieldValue?.length === 0);

              if (isEmpty) {
                invalidFields?.push({
                  name: fieldName,
                  errors: ['This field is required'],
                });
              }

              if (field?.type === FIELD_TYPES.NUMERIC && fieldValue) {
                const numValue = Number(fieldValue);
                if (Number.isNaN(numValue)) {
                  invalidFields?.push({
                    name: fieldName,
                    errors: ['Please enter a valid number'],
                  });
                }
              }
            });

            if (invalidFields?.length > 0) {
              form.setFields(invalidFields);
              return;
            }

            const responses = createResponsesForEntry(
              rowIndex,
              allValues,
              record,
            );
            if (responses?.length > 0) {
              try {
                if (record?.id) {
                  await updateRegisterLogFormFieldEntry({
                    variables: {
                      updateRegisterLogFormFieldEntryId: record?.id,
                      data: {
                        isNewRow: false,
                        responses,
                      },
                    },
                  });
                  setTableData([...fieldsData.current]);
                } else if (!isCreatingEntry) {
                  setIsCreatingEntry(true);
                  try {
                    const result = await createRegisterLogFormFieldEntry({
                      variables: {
                        registerLogId,
                        data: {
                          filling: null,
                          tableEntries: responses,
                        },
                      },
                    });

                    if (result?.data?.createRegisterLogFormFieldEntry?.id) {
                      const newId =
                        result?.data?.createRegisterLogFormFieldEntry?.id;
                      const updatedData = [...fieldsData?.current];
                      updatedData[rowIndex] = {
                        ...updatedData[rowIndex],
                        id: newId,
                        key: newId,
                        isEmptyRow: false,
                      };
                      fieldsData.current = updatedData;
                      setTableData(updatedData);
                    }
                    if (record?.id) {
                      setEditingKeys((prev) => {
                        const newKeys = new Set(prev);
                        if (!record?.isEmptyRow) newKeys?.delete(record?.key);
                        return newKeys;
                      });
                    }
                  } finally {
                    setIsCreatingEntry(false);
                  }
                }
              } catch (error) {
                return error;
              }
            }
          }, 300);
        } catch (error) {
          handleTableRouterPromptUpdate(true);
          showRouterPrompt(true);
          return error;
        }
      },
      [tableHeaders, form],
    );

    const handleFieldsDataRefUpdate = (value, index, fieldName) => {
      if (index != null && fieldName != null && fieldName !== '') {
        fieldsData.current[index][fieldName] = value;
      } else {
        fieldsData.current = value;
      }
    };

    const handleFileListRefUpdate = (value, key) => {
      if (key) {
        fileListRef.current[key] = value;
      } else {
        fileListRef.current = value;
      }
    };

    const handleUploadImage = async (
      newFileList,
      fieldName,
      record,
      dataIndex,
      index,
    ) => {
      if (createEntryTimeoutRef?.current) {
        clearTimeout(createEntryTimeoutRef?.current);
      }
      const limitedFileList = newFileList?.slice(-3);

      const fileSend = (anotherFileList) => {
        return map(anotherFileList, (file) => {
          const fileType = file?.originFileObj?.type || file?.type;
          const fileName = file?.originFileObj?.name || file?.name;

          return {
            fileName: `register/attachments/${fileName}`,
            contentType: fileType,
          };
        });
      };
      const filteredFileList = filter(
        limitedFileList,
        (file) => file?.originFileObj?.name,
      );

      if (filteredFileList?.length) {
        try {
          fetchSignedUrl({
            variables: {
              data: fileSend(filteredFileList),
            },
          })?.then(({ data }) => {
            handleTableRouterPromptUpdate(true);
            showRouterPrompt(true);
            const keys = data?.getSignedPutUrls?.keys || [];

            if (fileListRef?.current?.[fieldName] && keys?.length > 0) {
              const updatedFileList = [...fileListRef?.current[fieldName]];

              filteredFileList?.forEach((file, i) => {
                if (keys[i]) {
                  const fileIndex = updatedFileList?.findIndex(
                    (f) => f?.uid === file?.uid,
                  );
                  if (fileIndex >= 0) {
                    updatedFileList[fileIndex] = {
                      ...updatedFileList[fileIndex],
                      key: keys[i],
                    };
                  }
                }
              });

              fileListRef.current[fieldName] = updatedFileList;

              if (fieldsData?.current?.[index]) {
                fieldsData.current[index] = {
                  ...fieldsData?.current[index],
                  [dataIndex]: updatedFileList?.map((file) => ({
                    uid: file?.uid,
                    status: file?.status,
                    url: file?.url,
                    value: file?.url,
                    key: file?.key,
                    name: file?.name,
                    thumbUrl: file?.thumbUrl,
                  })),
                };
              }
            }

            const uploadedKeys = imageKeys?.current?.[fieldName];
            if (uploadedKeys) {
              imageKeys.current[fieldName] = [...uploadedKeys, ...keys];
            } else {
              imageKeys.current[fieldName] = [...keys];
            }
            const key = `${index}_${dataIndex}`;
            form?.setFieldsValue({
              [key]: keys,
            });
            handleBlurSave(index, record);
          });
        } catch (error) {
          return error;
        }
      }
    };

    const handleOnChange = debounce(
      (info, fieldName, record, dataIndex, index) => {
        const newFileList = info?.fileList?.filter((file) => {
          const fileType = file?.originFileObj?.type || file?.type;
          if (
            !file?.url &&
            fileType &&
            !includes([...ALLOWED_IMAGE_TYPES, 'application/pdf'], fileType)
          ) {
            message?.destroy();
            message?.error('File type should be PNG, JPG, JPEG,PDF');
            return false;
          }

          return true;
        });
        if (!isMobileViewport) {
          handleFieldChange({ [fieldName]: newFileList });
        }
        tempUploadDataRef.current = newFileList;
        handleUploadImage(newFileList, fieldName, record, dataIndex, index);
      },
      300,
    );

    const handlePreview = async (file) => {
      let previewUrl = file?.url;
      if (file?.originFileObj) {
        previewUrl = URL.createObjectURL(file?.originFileObj);
      } else if (file?.thumbUrl && !file?.url) {
        previewUrl = file?.thumbUrl;
      }

      if (previewUrl) {
        // eslint-disable-next-line no-undef
        window?.open(previewUrl, '_blank');
        if (file?.originFileObj) {
          URL.revokeObjectURL(previewUrl);
        }
      }
    };

    const handleRowClick = (record, rowIndex, fieldName) => {
      if (isMobileViewport) {
        const updatedRecord = fieldsData?.current[rowIndex];
        setRowRecord(updatedRecord);
        setFieldIndex(rowIndex);
        setShowFillModal(true);
        setIsFieldClicked(fieldName);
      }
    };

    const isEditing = (record) => {
      if (!record) return;
      return editingKeys.has(record.key);
    };

    const handleScroll = (event) => {
      const { target } = event;
      const { scrollTop, scrollHeight, offsetHeight } = target || {};
      const scrolledToBottom =
        (scrollTop + offsetHeight) / scrollHeight >= SCROLL_PERCENT;
      if (scrolledToBottom && hasMore && !loading && !scrollFlag) {
        if (tableData?.length >= totalTableDataCount) return;
        setScrollFlag(true);
        getRegisterLogFormFieldsEntryList({
          variables: {
            registerLogId,
            filter: { ...initialPagination, skip: tableData?.length },
          },
        });
      }
    };

    const handleEditField = (field) => {
      setSelectedField(field);
      setShowModal(true);
    };

    const handleDeleteRecord = (record, index) => {
      setFieldIndex(index);
      setSelectedRecordToDelete(record);
      setShowDeleteRecordModal(true);
    };

    const columns = useMemo(
      () => [
        ...(!isEditControl
          ? [
              {
                title: '#',
                key: 'index',
                dataIndex: 'index',
                width: 100,
                render: (_, __, index) => index + 1,
              },
            ]
          : []),
        ...(tableHeaders?.length > 0
          ? tableHeaders?.map((field) => {
              return {
                ...field,
                dataIndex: toCamelCase(field?.name),
                key: toCamelCase(field?.name),
                id: field?.id,
                width: !isEditControl && 230,
                editable: true,
                onHeaderCell: (column) => ({
                  column,
                  dataIndex: field?.dataIndex,
                }),
                onCell: (record, rowIndex) => ({
                  record,
                  dataIndex: toCamelCase(field?.name),
                  title: field?.name,
                  fieldId: field?.id,
                  fieldType: field?.type,
                  editing: isEditing(record),
                  isRequired: field?.isRequired,
                  options: field?.options || [],
                  index: rowIndex,
                  fieldsData,
                  onFieldClick: () => {
                    if (isMobileViewport) {
                      handleRowClick(
                        record,
                        rowIndex,
                        `${rowIndex}_${toCamelCase(field?.name)}`,
                      );
                    }
                  },
                  isFieldClicked,
                  isMobileViewport,
                  isAuditor,
                  isUserAssignedToProject,
                  isOptionsFocused,
                  setIsOptionsFocused,
                  isMultiSelectOptionsFocused,
                  setIsMultiSelectOptionsFocused,
                  isDateFocused,
                  setIsDateFocused,
                  onDateOpenChange: async (e) => {
                    if (!e) {
                      try {
                        await form.validateFields({ validateOnly: true });
                        setIsDateFocused(null);
                        handleBlurSave(rowIndex, record);
                      } catch (error) {
                        handleTableRouterPromptUpdate(true);
                        showRouterPrompt(true);
                      }
                    }
                  },
                  isDisabled,
                  handleFieldChange,
                  handleBlurSave,
                  handleFileListRefUpdate,
                  form,
                  tableHeaders,
                  fileListRef,
                  deletingFile,
                  setDeletingFile,
                  uploadKey,
                  handleOnChange,
                  handleUploadRemove: async (file) => {
                    try {
                      if (deletingFile) return false;
                      setDeletingFile(true);
                      const fieldName = `${rowIndex}_${toCamelCase(field?.name)}`;
                      const currentFileList = [
                        ...(fileListRef?.current[fieldName] || []),
                      ];
                      const updatedFileList = currentFileList?.filter(
                        (f) => f?.uid !== file?.uid,
                      );
                      fileListRef.current[fieldName] = updatedFileList;
                      const currentField = fieldsData?.current?.[rowIndex];
                      if (!currentField) {
                        setDeletingFile(false);
                        return true;
                      }
                      const currentFiles =
                        currentField[toCamelCase(field?.name)] || [];
                      const remainingFiles = currentFiles?.filter((item) => {
                        return item?.key !== file?.key;
                      });
                      fieldsData.current[rowIndex] = {
                        ...currentField,
                        [toCamelCase(field?.name)]: remainingFiles,
                      };
                      form?.setFieldValue(fieldName, remainingFiles);
                      form?.validateFields([fieldName]);

                      const isFieldRequired =
                        field?.isRequired && !record?.isEmptyRow;
                      const willBeEmpty = !remainingFiles?.length;

                      if (isFieldRequired && willBeEmpty) {
                        setDeletingFile(false);
                        return false;
                      }

                      if (file?.key && imageKeys?.current?.[fieldName]) {
                        const fileNameWithoutSequence = file?.key?.replace(
                          REGEX.ID_WITH_PARENTHESES_AND_NUMBER,
                          ' $1',
                        );
                        imageKeys.current[fieldName] = imageKeys?.current?.[
                          fieldName
                        ]?.filter(
                          (key) =>
                            key?.replace(
                              REGEX.ID_WITH_PARENTHESES_AND_NUMBER,
                              ' $1',
                            ) !== fileNameWithoutSequence,
                        );
                      }

                      if (record?.id && (!isFieldRequired || !willBeEmpty)) {
                        const currentColumn = tableHeaders?.find(
                          (col) => col?.dataIndex === toCamelCase(field?.name),
                        );
                        if (currentColumn) {
                          const existingKeys = currentFiles
                            ?.filter((f) => f?.key && f?.key !== file?.key)
                            ?.map((f) => f?.key);
                          const newKeys = imageKeys?.current[fieldName] || [];
                          const allFileKeys = [
                            ...new Set([...existingKeys, ...newKeys]),
                          ];
                          const finalFileKeys = allFileKeys?.filter(
                            (key) =>
                              key !== file?.key &&
                              (!file?.name || !key?.includes(file?.name)),
                          );

                          try {
                            await updateRegisterLogFormFieldEntry({
                              variables: {
                                updateRegisterLogFormFieldEntryId: record?.id,
                                data: {
                                  isNewRow: false,
                                  responses: [
                                    {
                                      registerFormFieldId: currentColumn?.id,
                                      value: take(finalFileKeys, 3)?.join(','),
                                    },
                                  ],
                                },
                              },
                            });
                          } catch (error) {
                            message.destroy();
                            message.error('Failed to remove file');
                            setDeletingFile(false);
                            return error;
                          }
                        }
                      }

                      await form?.validateFields();
                      setDeletingFile(false);
                      setUploadKey((prev) => prev + 1);
                      return true;
                    } catch (error) {
                      handleTableRouterPromptUpdate(true);
                      showRouterPrompt(true);
                      return error;
                    }
                  },
                  handlePreview,
                  calculateFieldValue,
                  handleTableRouterPromptUpdate,
                }),
                render: (text) => {
                  if (Array.isArray(text)) {
                    if (text?.length > 0 && text?.some((item) => item?.key)) {
                      return (
                        <div className="d-flex align-center  gap-8">
                          <Image
                            src={
                              text[0]?.value ||
                              text[0]?.thumbUrl ||
                              text[0]?.url
                            }
                            height={50}
                            width={50}
                            fallback={FALLBACK_PDF_URL}
                          />
                          {text?.length - 1 > 0 && (
                            <span className="ml-5 pointer extra-count">
                              +{text?.length - 1}
                            </span>
                          )}
                        </div>
                      );
                    }
                    if (!text?.length) {
                      return '-';
                    }
                    return text?.join(',');
                  }
                  if (
                    field?.type === FIELD_TYPES.DATE &&
                    moment(text, DEFAULTDATEFORMAT)?.isValid()
                  ) {
                    return moment(text, DEFAULTDATEFORMAT)?.format(
                      DEFAULTDATEFORMAT,
                    );
                  }
                  if (includes(INVALID_FIELD_VALUES, text)) {
                    return '-';
                  }
                  return text;
                },
                title: (
                  <div className="width-percent-100 d-flex align-center justify-between gap-8">
                    {isEditControl && registerId && isEditable && (
                      <DragHandle />
                    )}
                    <div className="d-flex align-center">
                      {field?.isRequired && (
                        <div className="danger-text mr-5">*</div>
                      )}
                      <div className="mr-5">
                        <FieldIcon fieldType={field?.type} />
                      </div>
                      <div className="mr-5">{field?.name}</div>
                    </div>
                    {isEditControl && (
                      <div className="d-flex">
                        {isEditable ? (
                          <>
                            <EditButton
                              className="mr-10"
                              onClick={() => handleEditField(field)}
                            />
                            <DeleteButton
                              onClick={() => {
                                if (tableHeaders?.length === 1) {
                                  message.destroy();
                                  message.error(
                                    'There must be at-least 1 form field!',
                                  );
                                } else if (!isAuditor || isDisabled) {
                                  setSelectedField(field);
                                  setShowDeleteModal(true);
                                }
                              }}
                            />
                          </>
                        ) : (
                          <div
                            className="pointer text-underline mr-10"
                            onClick={() => handleEditField(field)}
                          >
                            View
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ),
                className: isEditControl ? 'drag-visible' : '',
              };
            })
          : []),
        // eslint-disable-next-line no-nested-ternary
        ...(isEditControl && isEditable
          ? [
              {
                key: 'addField',
                id: 'addField',
                title: (
                  <CanPerform
                    action={ALLOWED_ACTION_KEYS.ADD_PROJECT_REGISTER}
                    type={ALLOWED_ACTION_TYPE.BOTH}
                  >
                    <Button
                      type="link"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setSelectedField();
                        setShowModal(true);
                      }}
                    >
                      Add Field
                    </Button>
                  </CanPerform>
                ),
                dataIndex: 'addField',
                render: () => null,
                disableSort: true,
                className: '',
              },
            ]
          : []),

        ...(!isEditControl &&
        !isEditable &&
        CanPerform({
          action: ALLOWED_ACTION_KEYS.ADD_REGISTER_LOG,
          type: ALLOWED_ACTION_TYPE.BOTH,
        })
          ? [
              {
                key: 'actions',
                id: 'actions',
                dataIndex: 'action',
                width: 100,
                title: 'Actions',
                render: (_, record, index) => (
                  <div className="d-flex align-center width-percent-100">
                    <Button type="link" disabled={isDisabled}>
                      <DeleteButton
                        className="pointer"
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleDeleteRecord(record, index);
                        }}
                      />
                    </Button>
                  </div>
                ),
              },
            ]
          : []),
      ],
      [
        tableHeaders,
        isEditControl,
        isEditable,
        isMobileViewport,
        isOptionsFocused,
        isMultiSelectOptionsFocused,
        isDateFocused,
        editingKeys,
        isUserAssignedToProject,
      ],
    );

    const handleDragEnd = (event) => {
      const { active, over } = event;
      if (active && over) {
        updateRegisterFormFieldSequences({
          variables: {
            data: {
              registerId,
              registerFormFieldId: active?.id,
              sequence: over?.data?.current?.sortable?.index + 1,
            },
          },
        });
      }
      if (!active || !over || active?.id === over?.id) {
        return;
      }
      const oldIndex = tableHeaders?.findIndex((col) => col?.id === active?.id);
      const newIndex = tableHeaders?.findIndex((col) => col?.id === over?.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const newHeaders = arrayMove(tableHeaders, oldIndex, newIndex);
        setTableHeaders(newHeaders);
        const newTableColumns = arrayMove(tableColumns, oldIndex, newIndex);
        setTableColumns(newTableColumns);
      }
    };

    return (
      <>
        <CommonCard loading={isLoading}>
          <DndContext
            modifiers={[restrictToHorizontalAxis]}
            onDragEnd={handleDragEnd}
            sensors={sensors}
          >
            <SortableContext
              items={tableHeaders?.map((col) => col?.id)}
              strategy={horizontalListSortingStrategy}
            >
              <div className="width-percent-100">
                <h3>Table</h3>
                {!tableHeaders?.length > 0 && !isEditControl ? (
                  <Empty />
                ) : (
                  <>
                    {
                      //! giving inline style as need to generate image from this div
                    }
                    <div style={{ opacity: 0, height: 0 }}>
                      <div
                        id={ELEMENT_IDS.TIME_STAMP}
                        style={{
                          background: '#000',
                          color: '#3366ff',
                          width: 'max-content',
                        }}
                      >
                        {`${moment()?.format(DATETIMEWITHDIVIDE)} - ${currentProjectDetails?.timeZone}`}
                      </div>
                    </div>
                    <Form
                      form={form}
                      component={false}
                      onFieldsChange={() => {
                        if (!isFormChanged) {
                          setIsFormChanged(true);
                          const showTableFieldsPromptRef =
                            showTableFieldsPrompt;
                          if (
                            showTableFieldsPromptRef &&
                            showTableFieldsPromptRef.current !== undefined
                          ) {
                            showTableFieldsPromptRef.current = true;
                          }
                          showRouterPrompt(true);
                        }
                      }}
                    >
                      <div ref={tableRef}>
                        <CommonTable
                          components={{
                            header: { cell: Columns },
                            body: { cell: EditableCell },
                          }}
                          data={tableData}
                          className="register-table"
                          columns={columns}
                          scroll={{
                            x: 'max-content',
                            ...(!isEditControl && { y: 55 * 10 }),
                          }}
                          onRow={(record, index) => ({
                            onClick: () => {
                              if (
                                isMobileViewport &&
                                !isAuditor &&
                                isUserAssignedToProject &&
                                !isDisabled
                              ) {
                                setRowRecord(record);
                                setFieldIndex(index);
                                setShowFillModal(true);
                              } else if (!isEditing(record)) {
                                setEditingKeys((prev) => {
                                  const newSet = new Set(
                                    [...prev].filter((key) =>
                                      fieldsData?.current?.some(
                                        (row) =>
                                          row?.key === key && row?.isEmptyRow,
                                      ),
                                    ),
                                  );
                                  newSet.add(record.key);
                                  return newSet;
                                });

                                const emptyObj = Object.keys(record).reduce(
                                  (acc, key) => {
                                    if (key !== 'key' && key !== 'id') {
                                      acc[key] = Array.isArray(record[key])
                                        ? []
                                        : '';
                                    }
                                    return acc;
                                  },
                                  {},
                                );
                                form.setFieldsValue({ ...emptyObj, ...record });
                              }
                            },
                          })}
                          rowClassName="editable-row"
                          onScroll={handleScroll}
                        />
                      </div>
                    </Form>
                  </>
                )}
              </div>
            </SortableContext>
          </DndContext>
        </CommonCard>
        {showModal && (
          <AddFieldModal
            tableHeaders={tableHeaders}
            isDisabled={isAuditor || !isEditable}
            tableColumns={tableColumns}
            setTableColumns={setTableColumns}
            isEditControl={isEditControl}
            selectedField={selectedField}
            isUpdate={!!selectedField}
            numericFields={numericFields}
            setNumericFields={setNumericFields}
            showModal={showModal}
            setShowModal={setShowModal}
            setSelectedField={setSelectedField}
            handleRefetch={handleRegisterRefetch}
          />
        )}
        {showFillModal && (
          <AddEditTableFieldModal
            tableForm={form}
            tableHeaders={tableHeaders}
            tableData={tableData}
            setTableData={setTableData}
            fieldsData={fieldsData}
            rowRecord={rowRecord}
            setRowRecord={setRowRecord}
            fieldIndex={fieldIndex}
            setFieldIndex={setFieldIndex}
            showModal={showFillModal}
            setShowModal={setShowFillModal}
            handlePreview={handlePreview}
            fileListRef={fileListRef}
            isFieldClicked={isFieldClicked}
            handleTableRouterPromptUpdate={handleTableRouterPromptUpdate}
            handleFieldsDataRefUpdate={handleFieldsDataRefUpdate}
            handleFileListRefUpdate={handleFileListRefUpdate}
            editingKeys={editingKeys}
            setEditingKeys={setEditingKeys}
          />
        )}
        {showDeleteModal && (
          <DeleteTableFieldModal
            showModal={showDeleteModal}
            setShowModal={setShowDeleteModal}
            selectedField={selectedField}
            setSelectedField={setSelectedField}
            handleRefetch={handleRegisterRefetch}
          />
        )}
        {showDeleteRecordModal && (
          <DeleteFormFieldRecordModal
            showModal={showDeleteRecordModal}
            setShowModal={setShowDeleteRecordModal}
            selectedRecordToDelete={selectedRecordToDelete}
            setSelectedRecordToDelete={setSelectedRecordToDelete}
            fieldIndex={fieldIndex}
            tableHeaders={tableHeaders}
            tableData={tableData}
            setTableData={setTableData}
            fieldsData={fieldsData}
            form={form}
            showRouterPrompt={showRouterPrompt}
            fileListRef={fileListRef}
            handleFieldsDataRefUpdate={handleFieldsDataRefUpdate}
            handleFileListRefUpdate={handleFileListRefUpdate}
          />
        )}
      </>
    );
  },
);

export default AddEditRegisterFieldsTable;
